import React, { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import Navbar from "../../components/layout/Navbar";
import Footer from "../../components/layout/Footer";
import { fetchPurchaseHistory } from "../../redux/subscription";
import "../../components/styles/paymentCallbackPage.scss";

/**
 * PaymentCallbackPage — Return point from hosted payment gateways.
 *
 * Flow:
 *   1. User clicks a gateway on PickPlanPage → window.location.href = gateway URL
 *   2. Before redirecting, PickPlanPage stashes the in-flight context under
 *      `mango_pending_purchase` in localStorage so we can route the user back
 *      to their movie/details page once payment finishes.
 *   3. Gateway redirects here on completion (success OR failure) with status
 *      params on the query string. Param names vary by gateway, so we accept
 *      anything that looks affirmative/negative.
 *   4. We refresh purchase history from CRM, show a success/failure state,
 *      and route the user back to where they were heading.
 */

const PENDING_PURCHASE_KEY = "mango_pending_purchase";

// Gateway query-param values that mean "this worked".
const SUCCESS_TOKENS = new Set([
  "successful",
  "success",
  "ok",
  "completed",
  "paid",
  "approved",
]);

const FAILURE_TOKENS = new Set([
  "failed",
  "failure",
  "cancelled",
  "canceled",
  "declined",
  "error",
]);

// True if the CRM orders list now contains a currently-Active order for the
// product the user just tried to buy. This — NOT the gateway's query-string
// status — is the real proof of payment: when a user closes/cancels the hosted
// checkout, the gateway still redirects back here, but no Active order is ever
// created, so this returns false.
export const hasActiveOrderForProduct = (orders, productId) => {
  if (!Array.isArray(orders) || productId == null || productId === "") return false;
  return orders.some(
    (o) =>
      (o.status === "Active" || o.status === "active") &&
      String(o.product_id) === String(productId)
  );
};

const sleep = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

const readStatus = (params) => {
  // Each gateway names the status param differently — Flutterwave uses `status`,
  // Paystack uses `status` or sometimes nothing (you verify via `reference`),
  // Seerbit/Monnify use `status` or `paymentStatus`. Accept any of them.
  const raw =
    params.get("status") ||
    params.get("paymentStatus") ||
    params.get("payment_status") ||
    params.get("transaction_status") ||
    "";
  const value = raw.toLowerCase();
  if (SUCCESS_TOKENS.has(value)) return "success";
  if (FAILURE_TOKENS.has(value)) return "failure";
  return "unknown";
};

const PaymentCallbackPage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [phase, setPhase] = useState("processing"); // processing | success | failure
  const [message, setMessage] = useState("Verifying your payment…");
  const [pending, setPending] = useState(null);

  // Mount marker — if you don't see this in the browser console when visiting
  // /payment-callback, the deployed bundle is missing the route and the
  // wildcard fallback is rendering instead.
  console.log("[PaymentCallback] mounted at", window.location.href);

  useEffect(() => {
    let cancelled = false;

    const finalize = async () => {
      // Pull whatever we stashed before redirecting to the gateway. May be null
      // if the user opened /payment-callback directly.
      let stash = null;
      try {
        const raw = localStorage.getItem(PENDING_PURCHASE_KEY);
        if (raw) stash = JSON.parse(raw);
      } catch (e) {
        console.warn("[PaymentCallback] Could not parse pending purchase:", e.message);
      }
      if (!cancelled) setPending(stash);

      const reportedStatus = readStatus(searchParams);
      const txRef =
        searchParams.get("tx_ref") ||
        searchParams.get("reference") ||
        searchParams.get("paymentReference") ||
        searchParams.get("transactionReference") ||
        "";

      console.log("[PaymentCallback] Returned from gateway:", {
        reportedStatus,
        txRef,
        gateway: stash?.gateway,
        productId: stash?.productId,
        plan: stash?.plan,
        rawParams: Object.fromEntries(searchParams.entries()),
      });

      // Gateway explicitly told us it failed — believe it, no need to poll.
      if (reportedStatus === "failure") {
        setPhase("failure");
        setMessage("Your payment didn't go through. You haven't been charged.");
        return;
      }

      // The CRM is the ONLY reliable source of truth. The gateway's query-string
      // status is unreliable — when the user closes/cancels Seerbit's hosted
      // checkout it still redirects here with no failure token (readStatus →
      // "unknown"), so we must NOT treat "unknown" as paid. Instead, confirm an
      // Active order actually exists for the product they tried to buy.
      //
      // A genuinely successful payment can take a moment to propagate to the CRM
      // (gateway webhook → billing), so poll a few times before giving up. A
      // cancelled checkout never creates an Active order, so it polls out and
      // correctly lands on the failure state.
      const productId = stash?.productId ?? null;
      let confirmed = false;

      // If the gateway DID send an explicit success token we can trust it as a
      // positive signal even before CRM propagation — but we still refresh
      // history so entitlement state is up to date for the player.
      const gatewaySaysSuccess = reportedStatus === "success";

      const POLL_ATTEMPTS = 5;
      const POLL_DELAY_MS = 1500;
      for (let attempt = 1; attempt <= POLL_ATTEMPTS; attempt += 1) {
        let orders = [];
        try {
          orders = await fetchPurchaseHistory("Active");
        } catch (e) {
          console.warn("[PaymentCallback] Purchase history refresh failed:", e.message);
        }
        if (cancelled) return;

        confirmed = hasActiveOrderForProduct(orders, productId);
        console.log("[PaymentCallback] entitlement check", {
          attempt,
          productId,
          confirmed,
          gatewaySaysSuccess,
          activeOrders: Array.isArray(orders)
            ? orders.filter((o) => o.status === "Active" || o.status === "active").map((o) => o.product_id)
            : orders,
        });

        if (confirmed) break;
        // Gateway gave no explicit token AND we have no productId to verify
        // against — nothing more polling can prove, so stop early and fail safe.
        if (!gatewaySaysSuccess && productId == null) break;
        if (attempt < POLL_ATTEMPTS) {
          setMessage("Confirming your payment…");
          await sleep(POLL_DELAY_MS);
          if (cancelled) return;
        }
      }

      if (!confirmed && !gatewaySaysSuccess) {
        // No CRM entitlement and no gateway success token → payment was not
        // completed (most commonly: user closed the checkout). Fail safe.
        setPhase("failure");
        setMessage(
          "We couldn't confirm your payment, so you haven't been charged. " +
            "If you did complete payment, please wait a moment and try again."
        );
        return;
      }

      setPhase("success");
      setMessage(
        stash?.planLabel
          ? `${stash.planLabel} is now active. Enjoy!`
          : "Payment received. Your plan is now active."
      );

      // Clear the stash so a refresh doesn't loop back through this page.
      localStorage.removeItem(PENDING_PURCHASE_KEY);
    };

    finalize();
    return () => {
      cancelled = true;
    };
  }, [searchParams]);

  // Route the user back to the most specific page we know about, falling back
  // progressively. Priority is:
  //   1. The specific movie the user was trying to play (/player/<uid>)
  //   2. The movie details page they came from (/details/<id>)
  //   3. An explicit returnPath the source page passed (e.g. /podcast/<id>)
  //   4. The category page for the package they just paid for —
  //      /discover/product/<productId>. Falls out of the stash even when the
  //      source page didn't set any movie/path context.
  //   5. Last resort: /home.
  const continueToContent = () => {
    if (pending?.returnMovieUid) {
      // Use the stashed content type — an episode id reopened as ?type=movie
      // makes the player resolve against the wrong endpoint and hang on
      // "Loading…" forever. Default to "movie" for legacy stashes.
      const returnType = pending.returnType || "movie";
      console.log("[PaymentCallback] continue → player", pending.returnMovieUid, returnType);
      navigate(`/player/${pending.returnMovieUid}?type=${returnType}`, {
        state: { movieId: pending.returnMovieId },
      });
      return;
    }
    if (pending?.returnMovieId) {
      console.log("[PaymentCallback] continue → movie details", pending.returnMovieId);
      navigate(`/details/${pending.returnMovieId}`);
      return;
    }
    if (pending?.returnPath) {
      console.log("[PaymentCallback] continue → returnPath", pending.returnPath);
      navigate(pending.returnPath);
      return;
    }
    if (pending?.productId) {
      console.log("[PaymentCallback] continue → package page", pending.productId);
      navigate(`/discover/product/${pending.productId}`);
      return;
    }
    console.log("[PaymentCallback] continue → /home (no stash context)");
    navigate("/home");
  };

  const retry = () => {
    // Send the user back to the pick-plan page they started on if we know it.
    if (pending?.productId) {
      navigate(`/discover/product/${pending.productId}/plan`);
    } else {
      navigate("/discover");
    }
  };

  return (
    <div className="payment-callback">
      <Navbar activeTab="Discover" />

      <div className="payment-callback__body">
        <div className="payment-callback__card">
          {phase === "processing" && (
            <>
              <div className="payment-callback__spinner" aria-hidden="true" />
              <h1 className="payment-callback__title">Verifying your payment</h1>
              <p className="payment-callback__message">{message}</p>
            </>
          )}

          {phase === "success" && (
            <>
              <div className="payment-callback__icon payment-callback__icon--success" aria-hidden="true">
                <svg width="48" height="48" viewBox="0 0 24 24" fill="none">
                  <circle cx="12" cy="12" r="11" stroke="#22B573" strokeWidth="1.5" fill="#22B573" />
                  <path d="M7 12.5L10.5 16L17 9" stroke="#fff" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
              </div>
              <h1 className="payment-callback__title">Payment successful</h1>
              <p className="payment-callback__message">{message}</p>
              <button className="payment-callback__cta" onClick={continueToContent}>
                Continue
              </button>
            </>
          )}

          {phase === "failure" && (
            <>
              <div className="payment-callback__icon payment-callback__icon--failure" aria-hidden="true">
                <svg width="48" height="48" viewBox="0 0 24 24" fill="none">
                  <circle cx="12" cy="12" r="11" stroke="#E62B2B" strokeWidth="1.5" fill="#E62B2B" />
                  <path d="M8 8L16 16M16 8L8 16" stroke="#fff" strokeWidth="2.2" strokeLinecap="round" />
                </svg>
              </div>
              <h1 className="payment-callback__title">Payment failed</h1>
              <p className="payment-callback__message">{message}</p>
              <div className="payment-callback__actions">
                <button className="payment-callback__cta" onClick={retry}>
                  Try again
                </button>
                <button
                  className="payment-callback__cta payment-callback__cta--ghost"
                  onClick={() => navigate("/home")}
                >
                  Back to Home
                </button>
              </div>
            </>
          )}
        </div>
      </div>

      <Footer />
    </div>
  );
};

export default PaymentCallbackPage;
